import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Star, StickyNote, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Star toggle + private note editor for a single entry, shown on the detail page.
export const FavoriteControls = ({ slug, name }) => {
  const { user, favorites, isFavorite, toggleFavorite, saveNote, setStatus } = useAuth();
  const navigate = useNavigate();
  const fav = isFavorite(slug);
  const status = favorites[slug]?.status || "wishlist";
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setNote(favorites[slug]?.note || ""); }, [favorites, slug]);

  const onToggle = async () => {
    if (!user) {
      toast.info("Sign in to save favorites", { action: { label: "Sign in", onClick: () => navigate("/account") } });
      return;
    }
    setBusy(true);
    try {
      const r = await toggleFavorite(slug, note);
      toast.success(r.favorited ? `Saved “${name}” to favorites` : `Removed “${name}” from favorites`);
      if (r.favorited) setEditing(true);
    } catch { toast.error("Something went wrong"); } finally { setBusy(false); }
  };

  const onSaveNote = async () => {
    setSaving(true);
    try { await saveNote(slug, note); toast.success("Note saved"); setEditing(false); }
    catch { toast.error("Could not save note"); } finally { setSaving(false); }
  };

  const onStatus = async (s) => {
    try { await setStatus(slug, s); toast.success(s === "visited" ? "Marked as visited" : "Moved to your wishlist"); }
    catch { toast.error("Could not update"); }
  };

  return (
    <div data-testid="favorite-controls" className="border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          data-testid="favorite-toggle"
          onClick={onToggle}
          disabled={busy}
          className={`flex items-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-widest border transition-colors ${
            fav ? "bg-accent text-accent-foreground border-accent" : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
          }`}
        >
          <Star className={`h-4 w-4 ${fav ? "fill-current" : ""}`} />
          {fav ? "Saved" : "Save to Favorites"}
        </button>
        {fav && (
          <div className="flex border border-border" data-testid="favorite-status-toggle">
            <button
              data-testid="favorite-status-wishlist"
              onClick={() => onStatus("wishlist")}
              className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors"
              style={{ background: status === "wishlist" ? "#D97706" : "transparent", color: status === "wishlist" ? "#fff" : "hsl(var(--muted-foreground))" }}
            >
              Want to go
            </button>
            <button
              data-testid="favorite-status-visited"
              onClick={() => onStatus("visited")}
              className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-l border-border transition-colors"
              style={{ background: status === "visited" ? "#4D7C0F" : "transparent", color: status === "visited" ? "#fff" : "hsl(var(--muted-foreground))" }}
            >
              Visited
            </button>
          </div>
        )}
        {fav && !editing && (
          <button
            data-testid="favorite-note-edit"
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors"
          >
            <StickyNote className="h-3.5 w-3.5" /> {note ? "Edit note" : "Add a note"}
          </button>
        )}
      </div>

      {fav && !editing && note && (
        <p data-testid="favorite-note-display" className="mt-3 text-sm text-foreground/90 border-l-2 border-accent pl-3 whitespace-pre-wrap">{note}</p>
      )}

      {fav && editing && (
        <div className="mt-3">
          <textarea
            data-testid="favorite-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Private note — why you want to visit, tips, what to look for…"
            className="w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent resize-y"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              data-testid="favorite-note-save"
              onClick={onSaveNote}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-2 font-mono text-[11px] uppercase tracking-widest bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save note
            </button>
            <button
              onClick={() => { setNote(favorites[slug] || ""); setEditing(false); }}
              className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
